import { useState } from 'react'
import {
  Card, Table, Button, Space, Typography, Select, Tag,
  Drawer, Form, Input, message, Row, Col,
} from 'antd'
import { ShopOutlined, PlusOutlined, EditOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title } = Typography
const API = ''

const WORK_TYPES = ['HOUSEKEEPING', 'MAINTENANCE', 'CIVIL', 'ELECTRICAL', 'LOADING', 'OTHER']

const STATUS_COLORS: Record<string, string> = {
  ACTIVE: 'green',
  BLACKLISTED: 'red',
  INACTIVE: 'default',
}

export default function ContractorPage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const qc = useQueryClient()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useState<any>(null)
  const [form] = Form.useForm()

  const { data: contractors, isLoading } = useQuery({
    queryKey: ['contractors', plantId],
    queryFn: () =>
      apiClient.get(`${API}/contractors`, { params: { plant_id: plantId } }).then(r => r.data),
    enabled: !!plantId,
  })

  const createMut = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post(`${API}/contractors`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contractors'] })
      message.success('Contractor created')
      setDrawerOpen(false)
      form.resetFields()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiClient.patch(`${API}/contractors/${id}`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contractors'] })
      message.success('Contractor updated')
      setDrawerOpen(false)
      setEditing(null)
      form.resetFields()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const openCreate = () => {
    setEditing(null)
    form.resetFields()
    form.setFieldValue('plant_id', plantId)
    setDrawerOpen(true)
  }

  const openEdit = (record: any) => {
    setEditing(record)
    form.setFieldsValue({
      contractor_code: record.contractor_code,
      contractor_name: record.contractor_name,
      work_type: record.work_type,
      contact_person: record.contact_person,
      mobile: record.mobile,
      remarks: record.remarks,
      status: record.status,
    })
    setDrawerOpen(true)
  }

  const handleSubmit = () => {
    form.validateFields().then(vals => {
      if (editing) {
        updateMut.mutate({ id: editing.id, data: vals })
      } else {
        createMut.mutate({ ...vals, plant_id: plantId })
      }
    })
  }

  const list = Array.isArray(contractors) ? contractors : []

  const columns = [
    {
      title: 'Code',
      dataIndex: 'contractor_code',
      key: 'contractor_code',
      width: 120,
      render: (v: string) => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span>,
    },
    { title: 'Name', dataIndex: 'contractor_name', key: 'contractor_name' },
    {
      title: 'Work Type',
      dataIndex: 'work_type',
      key: 'work_type',
      width: 140,
      render: (v: string) => <Tag>{v}</Tag>,
    },
    { title: 'Contact Person', dataIndex: 'contact_person', key: 'contact_person', width: 150 },
    { title: 'Mobile', dataIndex: 'mobile', key: 'mobile', width: 130 },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (v: string) => <Tag color={STATUS_COLORS[v] ?? 'default'}>{v}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 70,
      render: (_: unknown, r: any) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
      ),
    },
  ]

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <ShopOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        Contractors
      </Title>
      <Typography.Text type="secondary">Manage contractor master and status</Typography.Text>

      <Card style={{ marginTop: 16 }}>
        <Row gutter={12} style={{ marginBottom: 16 }} justify="space-between">
          <Col>
            <Select
              value={plantId}
              onChange={setPlantId}
              style={{ width: 200 }}
              options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))}
            />
          </Col>
          <Col>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Add Contractor
            </Button>
          </Col>
        </Row>

        <Table
          dataSource={list}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          size="small"
          pagination={{ pageSize: 50 }}
          scroll={{ x: 900 }}
        />
      </Card>

      <Drawer
        title={editing ? `Edit Contractor: ${editing.contractor_name}` : 'Add Contractor'}
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setEditing(null); form.resetFields() }}
        width={440}
        footer={
          <Row justify="end">
            <Space>
              <Button onClick={() => { setDrawerOpen(false); setEditing(null); form.resetFields() }}>Cancel</Button>
              <Button
                type="primary"
                loading={createMut.isPending || updateMut.isPending}
                onClick={handleSubmit}
              >
                {editing ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Row>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item name="contractor_code" label="Contractor Code" rules={[{ required: true }]}>
            <Input placeholder="e.g. CON-001" />
          </Form.Item>
          <Form.Item name="contractor_name" label="Contractor Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="work_type" label="Work Type" rules={[{ required: true }]}>
            <Select options={WORK_TYPES.map(t => ({ value: t, label: t }))} />
          </Form.Item>
          <Form.Item name="contact_person" label="Contact Person">
            <Input />
          </Form.Item>
          <Form.Item name="mobile" label="Mobile">
            <Input />
          </Form.Item>
          {editing && (
            <Form.Item name="status" label="Status">
              <Select
                options={['ACTIVE', 'INACTIVE', 'BLACKLISTED'].map(s => ({ value: s, label: s }))}
              />
            </Form.Item>
          )}
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  )
}
